import { ProductEnum } from "../../constants/login";
import { ExcelImportMeta } from "../types";

/**
 * Product import order registry (worksheet tab name → sequence metadata).
 *
 * | Worksheet           | Order | Phase        | Depends on                          |
 * |---------------------|-------|--------------|-------------------------------------|
 * See GOLF / SPA / VERSA tables below — used by getOrderedImportPlan().
 *
 * Worksheet-level config (importOrder on excelWorksheet) overrides this registry.
 */

/** Golf — Visual One import sequence */
export const GOLF_EXCEL_IMPORT_ORDER: Record<string, ExcelImportMeta> = {
  Outlet: { importOrder: 10, importPhase: "foundation" },
  Category: { importOrder: 20, importPhase: "retail-base" },
  CategoryGroup: { importOrder: 25, importPhase: "retail-base" },
  SubCategory: { importOrder: 30, importPhase: "retail-base" },
  RetailItem: {
    importOrder: 35,
    importPhase: "retail-base",
    dependsOn: ["Outlet", "Category"],
  },
  Course: { importOrder: 40, importPhase: "foundation", dependsOn: ["Outlet"] },
  AllocationBlock: { importOrder: 50, importPhase: "masters" },
  PlayerType: { importOrder: 60, importPhase: "masters" },
  RateType: { importOrder: 70, importPhase: "masters" },
  PlayerTypeMapping: {
    importOrder: 80,
    importPhase: "links",
    dependsOn: ["AllocationBlock", "PlayerType"],
  },
  RateTypeMapping: {
    importOrder: 81,
    importPhase: "links",
    dependsOn: ["PlayerType", "RateType"],
  },
  RatePlan: {
    importOrder: 90,
    importPhase: "transactions",
    dependsOn: ["Course", "RateType", "RetailItem"],
  },
  Tax: { importOrder: 100, importPhase: "retail-ops", dependsOn: ["Outlet"] },
  DiscountTypes: { importOrder: 110, importPhase: "retail-ops" },
  DiscountReasons: { importOrder: 111, importPhase: "retail-ops" },
  QuickSaleCategory: {
    importOrder: 120,
    importPhase: "retail-ops",
    dependsOn: ["Outlet"],
  },
  VendorType: { importOrder: 130, importPhase: "retail-ops" },
  Vendor: {
    importOrder: 140,
    importPhase: "retail-ops",
    dependsOn: ["VendorType"],
  },
  VendorContactInformation: {
    importOrder: 141,
    importPhase: "retail-ops",
    dependsOn: ["Vendor"],
  },
  LessonLocation: { importOrder: 150, importPhase: "reference" },
  CancellationReasons: { importOrder: 151, importPhase: "reference" },
};

/** Spa — Outlet + Category first; masters before services; service children after ServiceDetail */
export const SPA_EXCEL_IMPORT_ORDER: Record<string, ExcelImportMeta> = {
  Outlet: { importOrder: 10, importPhase: "foundation" },
  CategoryGroup: { importOrder: 15, importPhase: "retail-base" },
  Category: {
    importOrder: 20,
    importPhase: "retail-base",
    dependsOn: ["CategoryGroup"],
  },
  SubCategory: { importOrder: 30, importPhase: "retail-base" },
  RetailItem: {
    importOrder: 35,
    importPhase: "retail-base",
    dependsOn: ["Outlet", "Category"],
  },
  ServiceGroups: { importOrder: 40, importPhase: "foundation" },
  Equipments: { importOrder: 50, importPhase: "foundation" },
  Locations: { importOrder: 60, importPhase: "foundation" },
  PriceTypes: { importOrder: 70, importPhase: "foundation" },
  GuestType: { importOrder: 80, importPhase: "foundation" },
  DiscountTypes: { importOrder: 100, importPhase: "reference" },
  DiscountReasons: { importOrder: 101, importPhase: "reference" },
  CancelReason: { importOrder: 110, importPhase: "reference" },
  BreakTypes: { importOrder: 111, importPhase: "reference" },
  LostDeniedReason: { importOrder: 112, importPhase: "reference" },
  MedicalConditions: { importOrder: 113, importPhase: "reference" },
  LogType: { importOrder: 114, importPhase: "reference" },
  VendorType: { importOrder: 120, importPhase: "retail-ops" },
  Vendor: {
    importOrder: 130,
    importPhase: "retail-ops",
    dependsOn: ["VendorType"],
  },
  VendorContactInformation: {
    importOrder: 131,
    importPhase: "retail-ops",
    dependsOn: ["Vendor"],
  },
  Tax: { importOrder: 140, importPhase: "retail-ops", dependsOn: ["Outlet"] },
  QuickSaleCategory: {
    importOrder: 150,
    importPhase: "retail-ops",
    dependsOn: ["Outlet"],
  },
  PaymentMethod: { importOrder: 160, importPhase: "retail-ops" },
  /** RetailItem sheet exports onboarding Retail Items Setup (after Outlet + Category). */
  AddOn: {
    importOrder: 170,
    importPhase: "retail-ops",
    dependsOn: ["Category"],
  },
  Therapist: { importOrder: 180, importPhase: "staff" },
  TherapistContact: {
    importOrder: 181,
    importPhase: "staff",
    dependsOn: ["Therapist"],
  },
  ServiceTherapist: {
    importOrder: 182,
    importPhase: "staff",
    dependsOn: ["Therapist", "ServiceDetail"],
  },
  TherapistAddOn: {
    importOrder: 183,
    importPhase: "staff",
    dependsOn: ["Therapist"],
  },
  ServiceDetail: {
    importOrder: 200,
    importPhase: "services",
    dependsOn: ["ServiceGroups", "Locations", "PriceTypes", "Category"],
  },
  ServicePriceType: {
    importOrder: 212,
    importPhase: "service-children",
    dependsOn: ["ServiceDetail", "PriceTypes"],
  },
  ServiceEquipment: {
    importOrder: 211,
    importPhase: "service-children",
    dependsOn: ["ServiceDetail"],
  },
  ServiceSeasonalDate: {
    importOrder: 213,
    importPhase: "service-children",
    dependsOn: ["ServiceDetail"],
  },
  ServiceAvailableDays: {
    importOrder: 210,
    importPhase: "service-children",
    dependsOn: ["ServiceDetail"],
  },
  PackageDetail: {
    importOrder: 300,
    importPhase: "packages",
    dependsOn: ["ServiceGroups", "Category"],
  },
  PackageAvailableDays: {
    importOrder: 310,
    importPhase: "package-children",
    dependsOn: ["PackageDetail"],
  },
  PackageComponents: {
    importOrder: 311,
    importPhase: "package-children",
    dependsOn: ["PackageDetail", "ServiceDetail"],
  },
  PackageYield: {
    importOrder: 312,
    importPhase: "package-children",
    dependsOn: ["PackageDetail"],
  },
};

/**
 * Sales & Catering — Outlet/Category before RetailItem; masters before catalog items.
 * Retail Items Setup exports as "RetailItem" (moduleName "Retail").
 */
export const SNC_EXCEL_IMPORT_ORDER: Record<string, ExcelImportMeta> = {
  Outlet: { importOrder: 10, importPhase: "retail-base" },
  CategoryGroup: { importOrder: 15, importPhase: "retail-base" },
  Category: {
    importOrder: 20,
    importPhase: "retail-base",
    dependsOn: ["CategoryGroup"],
  },
  RetailItem: {
    importOrder: 35,
    importPhase: "retail-base",
    dependsOn: ["Outlet", "Category"],
  },
  Managers: { importOrder: 36, importPhase: "reference" },
  "Food Type": { importOrder: 40, importPhase: "masters" },
  "Unit Of Measure": { importOrder: 41, importPhase: "masters" },
  "Function Room Setup": { importOrder: 50, importPhase: "masters" },
  "Function Room Fee": { importOrder: 51, importPhase: "masters" },
  "Function Room Feature": { importOrder: 52, importPhase: "masters" },
  "Function Type": { importOrder: 53, importPhase: "masters" },
  "Sales Call Type": { importOrder: 60, importPhase: "reference" },
  "Sales Call Step": { importOrder: 61, importPhase: "reference" },
  "Arrival Type": { importOrder: 62, importPhase: "reference" },
  "Contact Type": { importOrder: 63, importPhase: "reference" },
  "Cancellation Codes": { importOrder: 64, importPhase: "reference" },
  "Cancellation Policy": { importOrder: 65, importPhase: "reference" },
  "Potential Code": { importOrder: 66, importPhase: "reference" },
  "Denial Code": { importOrder: 67, importPhase: "reference" },
  "Distribution List Code": { importOrder: 68, importPhase: "reference" },
  "Reserved By Code": { importOrder: 69, importPhase: "reference" },
  Holiday: { importOrder: 70, importPhase: "reference" },
  RoomAndTax: { importOrder: 71, importPhase: "reference" },
  Incidentals: { importOrder: 72, importPhase: "reference" },
  "Function Room": {
    importOrder: 80,
    importPhase: "rooms",
    dependsOn: ["Function Room Feature", "RetailItem"],
  },
  "Menu Item": {
    importOrder: 90,
    importPhase: "catalog",
    dependsOn: ["Food Type", "Unit Of Measure"],
  },
  Menu_Item_Measurement: {
    importOrder: 91,
    importPhase: "catalog-children",
    dependsOn: ["Menu Item"],
  },
  Menu_Item_Revenue_Type: {
    importOrder: 92,
    importPhase: "catalog-children",
    dependsOn: ["Menu Item", "RetailItem"],
  },
  SetupItem: { importOrder: 100, importPhase: "catalog" },
  FunctionItemMeasurement: {
    importOrder: 101,
    importPhase: "catalog-children",
    dependsOn: ["SetupItem"],
  },
  FunctionItemRevenuetype: {
    importOrder: 102,
    importPhase: "catalog-children",
    dependsOn: ["SetupItem", "RetailItem"],
  },
  BeverageItem: { importOrder: 110, importPhase: "catalog" },
  BeverageItemMeasurement: {
    importOrder: 111,
    importPhase: "catalog-children",
    dependsOn: ["BeverageItem"],
  },
  BeverageItemRevenuetype: {
    importOrder: 112,
    importPhase: "catalog-children",
    dependsOn: ["BeverageItem", "RetailItem"],
  },
  MiscellaneousItem: { importOrder: 120, importPhase: "catalog" },
  MiscellaneousItemMeasurement: {
    importOrder: 121,
    importPhase: "catalog-children",
    dependsOn: ["MiscellaneousItem"],
  },
  MiscellaneousItemRevenuetype: {
    importOrder: 122,
    importPhase: "catalog-children",
    dependsOn: ["MiscellaneousItem", "RetailItem"],
  },
  AudioVisualItem: { importOrder: 130, importPhase: "catalog" },
  AudioVisualItemMeasurement: {
    importOrder: 131,
    importPhase: "catalog-children",
    dependsOn: ["AudioVisualItem"],
  },
  AudioVisualItemRevenuetype: {
    importOrder: 132,
    importPhase: "catalog-children",
    dependsOn: ["AudioVisualItem", "RetailItem"],
  },
};

/** Versa (Room/PMS) — building & post types before dependents */
export const VERSA_EXCEL_IMPORT_ORDER: Record<string, ExcelImportMeta> = {
  "Tax Exempt Categories": { importOrder: 5, importPhase: "tax" },
  Building: { importOrder: 10, importPhase: "foundation" },
  "Room Feature": { importOrder: 20, importPhase: "foundation" },
  "Room Location": { importOrder: 21, importPhase: "foundation" },
  "HouseKeeping Sections": { importOrder: 22, importPhase: "foundation" },
  "Post Type": { importOrder: 25, importPhase: "post-types" },
  "Room Type Maintenance": {
    importOrder: 30,
    importPhase: "rooms",
    dependsOn: ["Building", "Post Type"],
  },
  "Room Definition": {
    importOrder: 40,
    importPhase: "rooms",
    dependsOn: ["Building", "Room Type Maintenance"],
  },
  Department: { importOrder: 50, importPhase: "masters" },
  Category: {
    importOrder: 60,
    importPhase: "masters",
    dependsOn: ["Department"],
  },
  "Guest Type": { importOrder: 90, importPhase: "reference" },
  Origins: { importOrder: 100, importPhase: "reference" },
  Tax: { importOrder: 110, importPhase: "tax", dependsOn: ["Post Type"] },
  "Segment 1": { importOrder: 120, importPhase: "segments" },
  "Segment 2": {
    importOrder: 121,
    importPhase: "segments",
    dependsOn: ["Guest Type"],
  },
  "Payment Method": {
    importOrder: 130,
    importPhase: "payment",
    dependsOn: ["Post Type"],
  },
};

const registryByProduct: Partial<
  Record<ProductEnum, Record<string, ExcelImportMeta>>
> = {
  [ProductEnum.GOLF]: GOLF_EXCEL_IMPORT_ORDER,
  [ProductEnum.SPA]: SPA_EXCEL_IMPORT_ORDER,
  [ProductEnum.SALES_AND_CATETING]: SNC_EXCEL_IMPORT_ORDER,
  [ProductEnum.VERSA]: VERSA_EXCEL_IMPORT_ORDER,
  [ProductEnum.TEST]: VERSA_EXCEL_IMPORT_ORDER,
};

export const getImportOrderRegistry = (
  productType: ProductEnum | undefined,
): Record<string, ExcelImportMeta> =>
  productType ? (registryByProduct[productType] ?? {}) : {};

export const DEFAULT_IMPORT_META: ExcelImportMeta = {
  importOrder: 999,
  importPhase: "unconfigured",
  dependsOn: [],
};
